import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Loader2, AlertTriangle, Inbox, RefreshCw, Clock, Thermometer,
    CircleGauge, Droplet, Waves, Flame, ClipboardList, CheckCircle2,
    LineChart, TrendingUp, ChevronDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

const NS_VALUE = -100;

// ============================================
// Утилиты
// ============================================
export const formatValue = (val, decimals = 3) => {
    if (val === null || val === undefined) return '—';
    if (val === NS_VALUE || val === -1) return 'НС';
    if (typeof val === 'number') {
        return val.toLocaleString('ru-RU', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    }
    const num = parseFloat(val);
    if (isNaN(num)) return '—';
    if (num === NS_VALUE || num === -1) return 'НС';
    return num.toLocaleString('ru-RU', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};

const PARAM_NAMES = {
    tekyshie: {
        temperatura: ["t1", "t2", "t3", "t4", "t5", "t6", "t7", "t8", "tx", "dt1", "dt2", "dt3", "dt4", "tokr"],
        davlenie: ["P1", "P2", "P3", "P4", "P5", "P6", "P7", "P8", "Px", "dP1", "dP2", "dP3", "dP4"],
        obemnui_rasxod: ["Go1", "Go2", "Go3", "Go4", "Go5", "Go6", "Go7", "Go8", "Gox", "dGo1", "dGo2", "dGo3", "dGo4"],
        massovui_rasxod: ["Gm1", "Gm2", "Gm3", "Gm4", "Gm5", "Gm6", "Gm7", "Gm8", "Gmx", "dGm1", "dGm2", "dGm3", "dGm4"],
        energi: ["Q1", "Q2", "Q3", "Q4", "Q5", "Q6", "Q7", "Q8", "Qa", "Qb", "Qc", "Qd"],
    },
    nacoplen: {
        volume: ["V1", "V2", "V3", "V4", "V5", "V6", "V7", "V8", "Vx", "dV1", "dV2", "dV3", "dV4"],
        massa: ["M1", "M2", "M3", "M4", "M5", "M6", "M7", "M8", "Mx", "dM1", "dM2", "dM3", "dM4"],
        energi: ["Q1", "Q2", "Q3", "Q4", "Q5", "Q6", "Q7", "Q8", "Qa", "Qb", "Qc", "Qd"],
    }
};

const GROUP_ICONS = {
    'Время и дата': Clock, 'Температура': Thermometer, 'Давление': CircleGauge,
    'Объемный расход': Droplet, 'Массовый расход': Waves, 'Тепловая энергия': Flame,
    'Нештатные ситуации': AlertTriangle, 'Объем': Droplet, 'Масса': Waves, 'Энергия': Flame,
};

const GROUP_COLORS = {
    'Время и дата': 'border-blue-500/30 text-blue-400', 'Температура': 'border-red-500/30 text-red-400',
    'Давление': 'border-cyan-500/30 text-cyan-400', 'Объемный расход': 'border-purple-500/30 text-purple-400',
    'Массовый расход': 'border-indigo-500/30 text-indigo-400', 'Тепловая энергия': 'border-orange-500/30 text-orange-400',
    'Нештатные ситуации': 'border-yellow-500/30 text-yellow-400', 'Объем': 'border-teal-500/30 text-teal-400',
    'Масса': 'border-indigo-500/30 text-indigo-400', 'Энергия': 'border-orange-500/30 text-orange-400',
};

const decodeErrorsBase64 = (base64Value) => {
    if (!base64Value || Array.isArray(base64Value) || typeof base64Value !== 'string') return [];
    try {
        const binaryString = atob(base64Value);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
        return bytes;
    } catch (err) {
        console.error('Ошибка декодирования ошибок:', err);
        return [];
    }
};

// ============================================
// Обработка нештатных ситуаций
// ============================================
const addTemperatureNS = (errByte, idx, name, addNS) => {
    if (idx >= 0 && idx <= 7 && (errByte & 0x01)) addNS(`АВАРИЯ — температура ${name} вне диапазона`);
    else if (idx === 8 && (errByte & 0x01)) addNS('АВАРИЯ — температура холодной воды вне диапазона');
    else if (idx >= 9 && idx <= 12) {
        if (errByte & 0x01) addNS(`АВАРИЯ — невозможно рассчитать разность температуры ${name}`);
        if (errByte & 0x02) addNS(`АВАРИЯ — разность температуры ${name} вне диапазона`);
    } else if (idx === 13 && (errByte & 0x01)) addNS('ПРЕДУПРЕЖДЕНИЕ — температура окр. среды вне диапазона');
};

const addDavlenieNS = (errByte, idx, name, addNS) => {
    if (idx >= 0 && idx <= 7 && (errByte & 0x01)) addNS(`ПРЕДУПРЕЖДЕНИЕ — давление ${name} вне диапазона, использовано значение при НС`);
    else if (idx === 8 && (errByte & 0x01)) addNS('ПРЕДУПРЕЖДЕНИЕ — давление холодной воды вне диапазона');
    else if (idx >= 9 && idx < 13) {
        if (errByte & 0x01) addNS(`АВАРИЯ — невозможно рассчитать разность давления ${name}`);
        if (errByte & 0x02) addNS(`ПРЕДУПРЕЖДЕНИЕ — отрицательная разность давления ${name}`);
    }
};

const addVolumeNS = (errByte, idx, name, addNS) => {
    if (idx >= 0 && idx <= 8) {
        if (errByte & 0x01) addNS(`ПРЕДУПРЕЖДЕНИЕ — расход ${name} выше максимального порога`);
        if (errByte & 0x02) addNS(`ПРЕДУПРЕЖДЕНИЕ — расход ${name} ниже переходного порога`);
        if (errByte & 0x04) addNS(`ПРЕДУПРЕЖДЕНИЕ — расход ${name} ниже минимального порога`);
        if (errByte & 0x08) addNS(`АВАРИЯ — расход ${name} выше максимального порога`);
        if (errByte & 0x10) addNS(`АВАРИЯ — расход ${name} ниже минимального порога`);
    } else if (idx >= 9 && idx < 13) {
        if (errByte & 0x01) addNS(`АВАРИЯ — невозможно рассчитать разность объемного расхода ${name}`);
        if (errByte & 0x02) addNS(`ПРЕДУПРЕЖДЕНИЕ — отрицательная разность объемного расхода ${name}`);
        if (errByte & 0x04) addNS(`АВАРИЯ — отрицательная разность объемного расхода ${name}`);
    }
};

const addMassaNS = (errByte, idx, name, addNS) => {
    if (idx >= 0 && idx <= 7 && (errByte & 0x01)) addNS(`АВАРИЯ — невозможно рассчитать массовый расход ${name}`);
    else if (idx === 8 && (errByte & 0x01)) addNS('АВАРИЯ — невозможно рассчитать массу холодной воды Gmx');
    else if (idx >= 9 && idx < 13) {
        if (errByte & 0x01) addNS(`АВАРИЯ — невозможно рассчитать разность массового расхода ${name}`);
        if (errByte & 0x02) addNS(`ПРЕДУПРЕЖДЕНИЕ — отрицательная разность массового расхода ${name}`);
        if (errByte & 0x04) addNS(`АВАРИЯ — отрицательная разность массового расхода ${name}`);
    }
};

const addEnergiNS = (errByte, idx, name, addNS) => {
    if (errByte & 0x01) addNS(`АВАРИЯ — невозможно рассчитать энергию теплоносителя ${name}`);
    if (errByte & 0x02) addNS(`ПРЕДУПРЕЖДЕНИЕ — отрицательная энергия теплоносителя ${name}`);
    if (errByte & 0x04) addNS(`АВАРИЯ — отрицательная энергия теплоносителя ${name}`);
};

// ============================================
// Построение групп показаний
// ============================================
const buildTekyshieGroups = (readings, config, podSystemIndex = 0) => {
    const groups = {};
    const nsItems = [];
    const addToGroup = (groupName, name, value, unit) => {
        if (!groups[groupName]) groups[groupName] = [];
        groups[groupName].push({ name, value, unit, isNS: value === 'НС' });
    };
    const addNS = (message) => nsItems.push({ name: message, value: '', unit: '', isNS: true });

    const errorsTemperatura = decodeErrorsBase64(readings.errors_temperatura);
    const errorsDavlenie = decodeErrorsBase64(readings.errors_davlenie);
    const errorsVolume = decodeErrorsBase64(readings.errors_volume);
    const errorsMassa = decodeErrorsBase64(readings.errors_massa);
    const errorsEnergi = decodeErrorsBase64(readings.errors_energi);

    for (let i = 0; i < config.length; i++) {
        const conf = config[i];
        if (conf === 0xA0) {
            const dt = readings.onDateTime ? new Date(readings.onDateTime).toLocaleString('ru-RU').replace(',', '') : 'Не определено';
            addToGroup('Время и дата', 'Показания на', dt, '');
        } else if (conf === 0xA1) {
            const val = readings.timeWork?.[podSystemIndex];
            if (val !== undefined) addToGroup('Время и дата', 'Наработка', formatValue(val), 'мин');
        } else if (conf === 0xA2) {
            const val = readings.timeNotWork?.[podSystemIndex];
            if (val !== undefined) addToGroup('Время и дата', 'Простой', formatValue(val), 'мин');
        } else if (conf >= 0x10 && conf < 0x20) {
            const idx = conf - 0x10;
            const name = PARAM_NAMES.tekyshie.temperatura[idx] || `t${idx + 1}`;
            const val = readings.temperatura?.[idx];
            addToGroup('Температура', name, val === NS_VALUE ? 'НС' : formatValue(val), '°C');
            addTemperatureNS(errorsTemperatura[idx] || 0, idx, name, addNS);
        } else if (conf >= 0x20 && conf < 0x30) {
            const idx = conf - 0x20;
            const name = PARAM_NAMES.tekyshie.davlenie[idx] || `P${idx + 1}`;
            const val = readings.davlenie?.[idx];
            addToGroup('Давление', name, val === NS_VALUE ? 'НС' : formatValue(val), 'МПа');
            addDavlenieNS(errorsDavlenie[idx] || 0, idx, name, addNS);
        } else if (conf >= 0x30 && conf < 0x40) {
            const idx = conf - 0x30;
            const name = PARAM_NAMES.tekyshie.obemnui_rasxod[idx] || `Go${idx + 1}`;
            const val = readings.obemnui_rasxod?.[idx];
            addToGroup('Объемный расход', name, val === NS_VALUE ? 'НС' : formatValue(val), 'м³/ч');
            addVolumeNS(errorsVolume[idx] || 0, idx, name, addNS);
        } else if (conf >= 0x40 && conf < 0x50) {
            const idx = conf - 0x40;
            const name = PARAM_NAMES.tekyshie.massovui_rasxod[idx] || `Gm${idx + 1}`;
            const val = readings.massovui_rasxod?.[idx];
            addToGroup('Массовый расход', name, val === NS_VALUE ? 'НС' : formatValue(val), 'т/ч');
            addMassaNS(errorsMassa[idx] || 0, idx, name, addNS);
        } else if (conf >= 0x50 && conf <= 0x60) {
            const idx = conf - 0x50;
            const name = PARAM_NAMES.tekyshie.energi[idx] || `Q${idx + 1}`;
            const val = readings.energi?.[idx];
            addToGroup('Тепловая энергия', name, val === NS_VALUE ? 'НС' : formatValue(val), 'Гкал/ч');
            addEnergiNS(errorsEnergi[idx] || 0, idx, name, addNS);
        }
    }
    if (nsItems.length > 0) groups['Нештатные ситуации'] = nsItems;
    return Object.entries(groups).map(([name, items]) => ({ name, items }));
};

const buildNacoplenGroups = (readings, config, podSystemIndex = 0) => {
    const groups = {};
    const addToGroup = (groupName, name, value, unit) => {
        if (!groups[groupName]) groups[groupName] = [];
        groups[groupName].push({ name, value, unit });
    };
    for (let i = 0; i < config.length; i++) {
        const conf = config[i];
        if (conf === 0xA0) {
            const dt = readings.onDateTime ? new Date(readings.onDateTime).toLocaleString('ru-RU').replace(',', '') : 'Не определено';
            addToGroup('Время и дата', 'Показания на', dt, '');
        } else if (conf === 0xA1) {
            const val = readings.timeWork?.[podSystemIndex];
            if (val !== undefined) addToGroup('Время и дата', 'Наработка', formatValue(val), 'час');
        } else if (conf === 0xA2) {
            const val = readings.timeNotWork?.[podSystemIndex];
            if (val !== undefined) addToGroup('Время и дата', 'Простой', formatValue(val), 'час');
        } else if (conf >= 0x30 && conf < 0x40) {
            const idx = conf - 0x30;
            const name = PARAM_NAMES.nacoplen.volume[idx] || `V${idx + 1}`;
            addToGroup('Объем', name, formatValue(readings.volume?.[idx]), 'м³');
        } else if (conf >= 0x40 && conf < 0x50) {
            const idx = conf - 0x40;
            const name = PARAM_NAMES.nacoplen.massa[idx] || `M${idx + 1}`;
            addToGroup('Масса', name, formatValue(readings.massa?.[idx]), 'т');
        } else if (conf >= 0x50 && conf <= 0x60) {
            const idx = conf - 0x50;
            const name = PARAM_NAMES.nacoplen.energi[idx] || `Q${idx + 1}`;
            addToGroup('Энергия', name, formatValue(readings.energi?.[idx]), 'Гкал');
        }
    }
    return Object.entries(groups).map(([name, items]) => ({ name, items }));
};

// ============================================
// Компоненты отображения
// ============================================
const ReadingsGroup = ({ group }) => {
    const [isOpen, setIsOpen] = useState(true);
    const isNSGroup = group.name === 'Нештатные ситуации';
    const IconComponent = GROUP_ICONS[group.name] || ClipboardList;
    const colorClass = GROUP_COLORS[group.name] || 'border-slate-700 text-slate-400';
    const [borderColor, iconColor] = colorClass.split(' ');

    return (
        <div className={`bg-slate-900 rounded-lg border ${borderColor} overflow-hidden mb-3`}>
            <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-800/50 transition">
                <div className="flex items-center gap-2">
                    <IconComponent className={`w-4 h-4 ${iconColor}`} />
                    <h3 className="text-white font-semibold text-xs sm:text-sm">{group.name}</h3>
                    <span className="text-slate-500 text-[10px] sm:text-xs">({group.items.length})</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
                <div className="border-t border-slate-700">
                    {group.items.length === 0 ? (
                        <div className="px-3 py-4 text-center text-slate-500 text-xs">
                            {isNSGroup ? (<div className="flex flex-col items-center gap-1"><CheckCircle2 className="w-6 h-6 text-green-500" /><span>Нештатных ситуаций нет</span></div>) : 'Нет данных'}
                        </div>
                    ) : isNSGroup ? (
                        <div className="divide-y divide-slate-700/50">
                            {group.items.map((item, idx) => (
                                <div key={idx} className="px-3 py-2 text-xs bg-red-500/10 hover:bg-red-500/20 transition flex items-start gap-2">
                                    <AlertTriangle className="w-3 h-3 text-red-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-red-200 font-medium flex-1">{item.name}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-12 gap-2 px-3 py-1.5 bg-slate-800/50 text-[10px] sm:text-xs text-slate-400 font-semibold">
                                <div className="col-span-6">Параметр</div>
                                <div className="col-span-4 text-center">Значение</div>
                                <div className="col-span-2 text-right">Ед. изм.</div>
                            </div>
                            <div className="divide-y divide-slate-700/50">
                                {group.items.map((item, idx) => (
                                    <div key={idx} className={`grid grid-cols-12 gap-2 px-3 py-1.5 text-xs hover:bg-slate-800/30 transition ${item.isNS ? 'bg-red-500/10' : ''}`}>
                                        <div className="col-span-6 text-slate-300 truncate" title={item.name}>{item.name}</div>
                                        <div className={`col-span-4 text-center font-mono ${item.isNS ? 'text-red-400 font-bold' : 'text-white'}`}>{item.value}</div>
                                        <div className="col-span-2 text-right text-slate-400 text-[10px]">{item.unit}</div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const TekyshieView = ({ readings, config, podSystemIndex }) => {
    if (!readings || !readings.onDateTime) return <div className="text-center py-6 text-slate-400 text-xs"><Inbox className="w-8 h-8 mx-auto mb-2 text-slate-600" /><p>Нет данных</p></div>;
    const groups = buildTekyshieGroups(readings, config, podSystemIndex);
    return <div className="space-y-2">{groups.map((group, idx) => <ReadingsGroup key={idx} group={group} />)}</div>;
};

const NacoplenView = ({ readings, config, podSystemIndex }) => {
    if (!readings || !readings.onDateTime) return <div className="text-center py-6 text-slate-400 text-xs"><Inbox className="w-8 h-8 mx-auto mb-2 text-slate-600" /><p>Нет данных</p></div>;
    const groups = buildNacoplenGroups(readings, config, podSystemIndex);
    return <div className="space-y-2">{groups.map((group, idx) => <ReadingsGroup key={idx} group={group} />)}</div>;
};

// ============================================
// Основной компонент попапа Аксиома-03
// ============================================
const Aksioma03PopupContent = ({ device }) => {
    const [activeSubTab, setActiveSubTab] = useState('tekyshie');
    const [selectedPodSystem, setSelectedPodSystem] = useState(0);
    const [readings, setReadings] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadReadings = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/api/devices/aksioma03/${device.name}/readings`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setReadings(response.data);
        } catch (err) {
            console.error('Ошибка загрузки показаний:', err);
            setError('Не удалось загрузить показания');
        } finally {
            setIsLoading(false);
        }
    }, [device.name]);

    useEffect(() => {
        loadReadings();
    }, [loadReadings]);

    if (isLoading) return <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin h-6 w-6 text-blue-500" /></div>;
    if (error) return <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-3 py-2 rounded-lg text-xs">{error}</div>;
    if (!readings) return <div className="text-center py-6 text-slate-400 text-xs"><Inbox className="w-8 h-8 mx-auto mb-2 text-slate-600" />Нет данных показаний</div>;

    const podSystems = readings.podSystems || [];
    const currentPodSystem = podSystems[selectedPodSystem];
    let config = [];
    if (currentPodSystem?.config) {
        if (typeof currentPodSystem.config === 'string') {
            try {
                const binaryString = atob(currentPodSystem.config);
                config = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) config[i] = binaryString.charCodeAt(i);
            } catch (e) { console.error('Base64 error', e); }
        } else if (Array.isArray(currentPodSystem.config)) {
            config = currentPodSystem.config;
        }
    }

    return (
        <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <label className="text-slate-400 text-xs font-medium whitespace-nowrap">Подсистема:</label>
                <select
                    value={selectedPodSystem}
                    onChange={(e) => setSelectedPodSystem(Number(e.target.value))}
                    className="flex-1 px-2 py-1.5 bg-slate-900 border border-slate-600 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                    disabled={podSystems.length === 0}
                >
                    {podSystems.length === 0 ? (<option value="0">Нет подсистем</option>) : (
                        podSystems.map((ps, idx) => (<option key={idx} value={idx}>{ps.name || `Подсистема ${idx + 1}`}</option>))
                    )}
                </select>
                <button onClick={loadReadings} className="px-2 py-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded text-xs font-medium transition flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" />
                </button>
            </div>

            <div className="flex border-b border-slate-700">
                <button onClick={() => setActiveSubTab('tekyshie')} className={`flex-1 px-3 py-2 text-xs font-medium transition flex items-center justify-center gap-1.5 ${activeSubTab === 'tekyshie' ? 'bg-purple-600/20 text-purple-400 border-b-2 border-purple-500' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'}`}>
                    <LineChart className="w-3 h-3" /> Текущие
                </button>
                <button onClick={() => setActiveSubTab('nacoplen')} className={`flex-1 px-3 py-2 text-xs font-medium transition flex items-center justify-center gap-1.5 ${activeSubTab === 'nacoplen' ? 'bg-purple-600/20 text-purple-400 border-b-2 border-purple-500' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'}`}>
                    <TrendingUp className="w-3 h-3" /> Накопленные
                </button>
            </div>

            <div className="max-h-[300px] overflow-y-auto custom-scrollbar pr-1">
                {podSystems.length === 0 ? (
                    <div className="text-center py-6 text-slate-400 text-xs"><Inbox className="w-8 h-8 mx-auto mb-2 text-slate-600" /><p>Нет данных подсистем учёта</p></div>
                ) : activeSubTab === 'tekyshie' ? (
                    <TekyshieView readings={readings.tekyshie} config={config} podSystemIndex={selectedPodSystem} />
                ) : (
                    <NacoplenView readings={readings.summarnue} config={config} podSystemIndex={selectedPodSystem} />
                )}
            </div>
        </div>
    );
};

export default Aksioma03PopupContent;